import { CheckoutDeliveryItem } from 'models/CheckoutDeliveryItem';
import connect from 'components/Shared/connect';
import { hasElements, rm } from 'utils';
import {
  getDeliveries,
  getUnfulfilledProducts,
  hasServicePointOptions,
  selectShouldUseNewCart,
  showDeliveries
} from 'views/CheckoutPage/DeliveryInformation/selector';
import { Delivery } from './delivery';
import { useDispatch, useSelector } from 'react-redux';
import { changeServicePoint } from 'views/CheckoutPage/DeliveryInformation/actions';
import batchAction from 'store/batching';
import { DeliveryOptionItem } from 'models/DeliveryOptionItem';
import styles from './styles.scss';
import { SectionTitle } from 'views/CheckoutPage/SectionTitle';
import {
  checkoutForm,
  SelectFulfilledInSystem,
  selectShouldUseIngrid
} from 'views/CheckoutPage/selector';
import cn from 'classnames';
import Spinner from 'components/Shared/Loader';
import { CheckoutModal } from 'views/CheckoutPage/checkoutModal';
import { Button } from 'components/Button';
import { useToggle } from 'hooks';
import { getCartItems, getSubItemRelations } from 'components/Cart/selector';
import { CartItem } from 'models/CartItem';
import Markdown from 'components/Shared/Markdown';
import { useEffect, useState } from 'react';
import { ProductUnfulfilled } from 'models/ProductUnfulfilled';
import { DeliveryWidget } from 'views/CheckoutPage/components/deliveryWidget';

export const SYSTEMTYPE_AX = 0;
export const SYSTEMTYPE_FLUENT = 1;

interface OwnProps {
  disabled?: boolean;
  rentalProductDatesInvalid?: boolean;
  shouldHideDeliveries?: boolean;
}

interface MergedProps extends OwnProps {
  showDeliveries: boolean;
  deliveries: CheckoutDeliveryItem[];
  selectedDeliveryInformation: {
    [key: string]: {
      selected: string;
    };
  };
}

const getUnfulfilledProductsContent = (
  unfulfilledProducts: ProductUnfulfilled[],
  items: CartItem[]
) => {
  return unfulfilledProducts.reduce((acc: React.ReactNode[], product) => {
    const cartItem = items.find(
      cartItem => cartItem.productId === product.sku || cartItem.productId === product.parentSku
    );

    if (!cartItem) return acc;

    const text =
      product.fulfilledQuantity === 0
        ? rm(
            'checkout_fulfilment_unfulfiled_product_none',
            product.requestedQuantity,
            cartItem?.quantity.unit,
            cartItem?.displayName
          )
        : rm(
            'checkout_fulfilment_unfulfiled_product',
            product.requestedQuantity,
            cartItem?.quantity.unit,
            cartItem?.displayName,
            product.fulfilledQuantity,
            cartItem?.quantity.unit
          );

    acc.push(
      <Markdown
        key={`${product.sku}-${acc.length}`}
        tag="div"
        content={text}
        className={styles.unfulfiledproduct}
      />
    );

    return acc;
  }, []);
};

const getShouldShowSpinner = (
  disabled: boolean | undefined,
  deliveries: CheckoutDeliveryItem[],
  unfulfilledProducts: ProductUnfulfilled[]
): boolean => {
  return !disabled && deliveries.length === 0 && unfulfilledProducts.length === 0;
};

const _DeliveryInformation = ({
  disabled,
  rentalProductDatesInvalid,
  deliveries,
  showDeliveries,
  shouldHideDeliveries,
  selectedDeliveryInformation
}: MergedProps) => {
  const dispatch = useDispatch();
  const isIngridFTActive = useSelector(selectShouldUseIngrid);
  const fulfilledInSystem = useSelector(SelectFulfilledInSystem);
  const unfulfilledProducts = useSelector(getUnfulfilledProducts);
  const [showUnfulfiledModal, toggleUnfulfiledModal] = useToggle(false);
  const shouldUseNewCart = useSelector(selectShouldUseNewCart);
  const cartItems = useSelector(getCartItems);
  const relations = useSelector(getSubItemRelations);
  const [showFallBack, setShowFallback] = useState(false);
  const relationItems = shouldUseNewCart
    ? Object.values(relations).flatMap(relation => relation.items)
    : [];

  const _items = [...cartItems, ...relationItems];

  const unfulfiledProductsContent = getUnfulfilledProductsContent(unfulfilledProducts, _items);

  const shouldUseIngrid = isIngridFTActive && fulfilledInSystem === SYSTEMTYPE_FLUENT;

  const shouldUseLegacyDeliveries =
    (!disabled && !hasElements(unfulfilledProducts) && showDeliveries && !shouldUseIngrid) ||
    showFallBack;

  useEffect(() => {
    if (unfulfilledProducts.length > 0) {
      toggleUnfulfiledModal();
    }
  }, [unfulfilledProducts.length, toggleUnfulfiledModal]);

  const onServicePointSelect = (
    deliveryId: string,
    deliveryOption: DeliveryOptionItem,
    syncServicePoint: boolean
  ) => {
    if (syncServicePoint) {
      const relatedDeliveries = getRelatedDeliveries(deliveries, deliveryId, deliveryOption).filter(
        d => {
          // remove related deliveries who does´nt have a servicepoint selected.
          const selectedDeliveryOptionId =
            selectedDeliveryInformation[`delivery-${d.identifier}`]?.selected;

          const selectedDeliveryOption = d.deliveryOptions?.find(
            o => o.deliveryOptionIdentifier === selectedDeliveryOptionId
          );

          return selectedDeliveryOption?.servicePointInformation !== undefined;
        }
      );

      const relatedDeliveryIds = getRelatedDeliveryIds(relatedDeliveries, deliveryOption);

      dispatch(
        batchAction(
          ...relatedDeliveryIds.map(({ deliveryId, deliveryOptionIdentifier }) =>
            changeServicePoint(deliveryId, deliveryOptionIdentifier)
          )
        )
      );
    }

    dispatch(changeServicePoint(deliveryId, deliveryOption.deliveryOptionIdentifier));
  };

  const getHasRelatedDeliveries = (delivery: CheckoutDeliveryItem) => {
    if (!hasServicePointOptions(delivery.deliveryOptions)) {
      return false;
    }
    const relatedDeliveries = getRelatedDeliveries(
      deliveries,
      delivery.identifier,
      delivery.deliveryOptions.find(d => d.servicePointInformation !== undefined) ||
        delivery.deliveryOptions[0]
    );

    return relatedDeliveries.length > 0;
  };

  const shouldShowSpinner = getShouldShowSpinner(disabled, deliveries, unfulfilledProducts);
  const isUnfulfilledProductsMessageShowing = !disabled && unfulfiledProductsContent.length > 0;
  const isIngridHidden = shouldShowSpinner || shouldHideDeliveries;
  const isLoadingIngridWidget =
    !disabled &&
    !isUnfulfilledProductsMessageShowing &&
    !shouldShowSpinner &&
    !shouldUseLegacyDeliveries &&
    isIngridHidden;

  return (
    <div
      className={cn(styles.deliveriesContainer, {
        [styles.disabled]: disabled
      })}
    >
      <CheckoutModal show={showUnfulfiledModal}>
        {unfulfiledProductsContent}
        <div className={styles.unfulfiledproduct}>{rm('checkout_fulfilment_update_cart')}</div>
        <Button onClick={toggleUnfulfiledModal} appearance="whitePill">
          {rm('shared_button_close')}{' '}
        </Button>
      </CheckoutModal>

      <SectionTitle title={rm('checkout_delivery_title')} />

      {disabled && (
        <div className={cn(styles.deliveries, styles.needAddress)}>
          {rm('checkout_delivery_need_adress_info')}
          {rentalProductDatesInvalid && <p>{rm('checkout_delivery_need_rental_dates')}</p>}
        </div>
      )}

      {/* if there are unfulfiledProducts, we should show the same information in Delivery section as the pop-up modal  */}
      {isUnfulfilledProductsMessageShowing && (
        <div className={cn(styles.deliveries, styles.needAddress)}>
          {unfulfiledProductsContent}
          <div className={styles.unfulfiledproduct}>{rm('checkout_fulfilment_update_cart')}</div>
        </div>
      )}

      {(shouldShowSpinner || isLoadingIngridWidget) && (
        <Spinner className={styles.spinner} loading />
      )}

      {shouldUseLegacyDeliveries && (
        <div className={styles.deliveries}>
          {deliveries.map(delivery => (
            <Delivery
              {...delivery}
              key={delivery.identifier}
              disabled={disabled}
              onServicePointSelect={onServicePointSelect}
              hasRelatedDeliveries={getHasRelatedDeliveries(delivery)}
            />
          ))}
        </div>
      )}

      {shouldUseIngrid && (
        <DeliveryWidget
          setShowFallback={setShowFallback}
          disabled={shouldShowSpinner || shouldHideDeliveries}
        />
      )}
    </div>
  );
};

const getRelatedDeliveries = (
  deliveries: CheckoutDeliveryItem[],
  deliveryId: string,
  deliveryOption: DeliveryOptionItem
) => {
  return deliveries.filter(
    delivery =>
      delivery.identifier !== deliveryId &&
      !!delivery.deliveryOptions.find(isRelatedDelivery(deliveryOption))
  );
};

const getRelatedDeliveryIds = (
  deliveries: CheckoutDeliveryItem[],
  selectedDeliveryOption: DeliveryOptionItem
) => {
  return deliveries.reduce((transformedDeliveries, delivery) => {
    return [
      ...transformedDeliveries,
      {
        deliveryId: delivery.identifier,
        deliveryOptionIdentifier: delivery.deliveryOptions.find(
          isRelatedDelivery(selectedDeliveryOption)
        )?.deliveryOptionIdentifier as string
      }
    ];
  }, []);
};

const isRelatedDelivery = (selectedDeliveryOption: DeliveryOptionItem) => {
  return (option: DeliveryOptionItem) =>
    !!selectedDeliveryOption.servicePointInformation &&
    option.servicePointInformation?.id === selectedDeliveryOption.servicePointInformation.id;
};

export const DeliveryInformation = connect<MergedProps, OwnProps>(state => ({
  deliveries: getDeliveries(state),
  showDeliveries: showDeliveries(state),
  selectedDeliveryInformation: checkoutForm(
    state,
    'deliveries'
  ) as MergedProps['selectedDeliveryInformation']
}))(_DeliveryInformation);
